import {register, CloudObject, Tag, Transaction, defineProperty, defineRelation} from 'olympe';

/**
     * @extends {CloudObject}
     */
export default class UserSettings extends CloudObject {
    /**
         * Creates an instance of UserSettings.
         *
         * @param {!Transaction} transaction
         * @return {!string} the tag of the created instance
         */
    static create(transaction) {
        return transaction.create(UserSettings);
    }

    /**
     * Create an instance of UserSettings, based on the properties of the filters given as a parameter
     * @param {Transaction} transaction
     * @param {string} dateFormat
     * @param {string} separator
     * @param {string} userId
     * @param {string} tag
     * @return {FavoriteFilters}
     */
    static createFromUserSettingsObject(transaction, dateFormat, separator, userId, tag) {
        const properties = new Map([
            [UserSettings.dateFormatProp, dateFormat],
            [UserSettings.userIdProp, userId],
            [UserSettings.separatorProp, separator],
        ]);
        if (tag) {
            // there is allready an instance of the user in DB so we just need to update it
            return transaction.update(tag, UserSettings.dateFormatProp, dateFormat).update(tag, UserSettings.separatorProp, separator);
        }
        return transaction.create(UserSettings, properties);
    }

    /**
     * Link the columns to display to the user settings
     * @param {Transaction} transaction
     * @param {UserSettings} userSettings
     * @param {ColumnsToDisplay} column
     */
    static linkColumnToDisplay(transaction, userSettings, column) {
        transaction.createRelation(UserSettings.columnsToDisplayRel, userSettings, column);
    }

    /**
     * Unlink the columns to display to the user settings
     * @param {Transaction} transaction
     * @param {UserSettings} userSettings
     * @param {ColumnsToDisplay} column
     */
    static unlinkColumnToDisplay(transaction, userSettings, column) {
        transaction.deleteRelation(UserSettings.columnsToDisplayRel, userSettings, column);
    }

    /**
     * @param {{id: string, description: string, email: string}} user
     * @return {LabelValueObject}
     */
    getUserSettingsObject() {
        const dateFormat = this.getDateFormat();
        const separator = this.getSeparator();
        const userId = this.getUserId();
        return {
            dateFormat: dateFormat,
            separator: separator,
            userId: userId || 'NONE',
        };
    }
    /**
     * @return {string}
     */
    getDateFormat() {
        return this.get(UserSettings.dateFormatProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeDateFormat($) {
        return this.observe($, UserSettings.dateFormatProp);
    }

    /**
     * @return {string}
     */
    getUserId() {
        return this.get(UserSettings.userIdProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeUserId($) {
        return this.observe($, UserSettings.userIdProp);
    }

    /**
     * @return {string}
     */
    getSeparator() {
        return this.get(UserSettings.separatorProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSeparator($) {
        return this.observe($, UserSettings.separatorProp);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setDateFormat(transaction, instance, value) {
        transaction.update(instance, UserSettings.dateFormatProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setUserId(transaction, instance, value) {
        transaction.update(instance, UserSettings.userIdProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSeparator(transaction, instance, value) {
        transaction.update(instance, UserSettings.separatorProp, value);
    }
}

// Hardcoded tags
register('018bfb72e9c539e7d7f4', UserSettings);
UserSettings.dateFormatProp = defineProperty('018bfb7339a3fc12f051');
UserSettings.userIdProp = defineProperty('018c0080a1bb722d032e');
UserSettings.separatorProp = defineProperty('018c103963ce7b4fee74');
UserSettings.columnsToDisplayRel = defineRelation('0190ee04c3937ca1df22');
